<template>
  <ak-display-table-row pointer>
    <ak-display-table-cell tag="router-link" :to="appLink" class="p-none">
      <ak-pack>
        <ak-pack-item class="palm--hidden">
          <div
            v-if="appType === 'pages'"
            class="c-screenshot width-100 h-75 bb b--white"
            :style="{
              'background-image': `url(${makePreviewImage(app, true)}`,
              'background-size': 'cover',
            }"
          />

          <div
            v-else
            class="c-screenshot width-100 h-75 bb- b--white text-center bb b--white flex flex--center flex--justify-center"
          >
            <ak-icon type="form" size="huge" color="white" />
          </div>
        </ak-pack-item>
        <ak-pack-item>
          <h4
            class="color-grey mb-none ak-pii mt-tiny text-breakall"
            :class="demoClass"
          >
            {{ app.title }}
          </h4>
          <p class="mb-none text-small">
            {{ app.campaignLabel }}
          </p>
        </ak-pack-item>
      </ak-pack>
    </ak-display-table-cell>

    <ak-display-table-cell tag="router-link" :to="appLink">
      <ak-app-status-tag :app="app" />
    </ak-display-table-cell>

    <ak-display-table-cell
      tag="router-link"
      :to="appLink"
      class="text-small min-width-100"
    >
      <em>Edited {{ fromNow(app.updatedOn) }}</em>
    </ak-display-table-cell>

    <ak-display-table-cell class="palm--hidden text-nowrap text-small">
      <div v-if="app.startDate" class="text-nowrap">
        Starts:
        <strong>{{ formatDate(app.startDate, 'DD/MM/YYYY') }}</strong>
      </div>
      <div v-if="app.endDate" class="text-nowrap">
        Ends:
        <strong>{{ formatDate(app.endDate, 'DD/MM/YYYY') }}</strong>
      </div>
    </ak-display-table-cell>

    <ak-display-table-cell class="text-center text-nowrap palm--hidden">
      <router-link
        class="color-text text-bold inline-block color-link-hover text-link"
        :class="demoClass"
        :to="{ name: 'singleAppLeads', params: { appId: app._id } }"
      >
        <span v-if="!app.leadCount || app.leadCount > 1"
          >{{ app.leadCount }} {{ leadsBrandingPlural }}</span
        >
        <span v-else>{{ app.leadCount }} {{ leadsBrandingSingle }}</span>
      </router-link>
    </ak-display-table-cell>

    <ak-display-table-cell class="text-right palm--hidden text-nowrap cell--1">
      <ak-button
        v-if="appType === 'pages'"
        size="tiny"
        color="grey-lighter"
        tag="a"
        :href="makePreviewUrl(app)"
        target="_blank"
      >
        Preview
        <ak-icon type="open" size="large" class="ml-tiny" />
      </ak-button>

      <ak-button
        v-if="appType === 'forms' && app.publishedStatus"
        size="tiny"
        color="grey-lighter"
        tag="a"
        :href="installLink(app)"
        target="_blank"
      >
        Install
        <ak-icon type="open" size="large" class="ml-tiny" />
      </ak-button>
    </ak-display-table-cell>

    <ak-display-table-cell
      v-if="appType === 'forms'"
      class="text-right palm--hidden text-nowrap cell--1"
    >
      <ak-tooltip v-if="app.publishedStatus">
        <template #trigger>
          <ak-button
            circle
            size="small"
            color="white"
            @click.stop="copyEmbedCode(app)"
          >
            <ak-icon type="code" size="big" />
          </ak-button>
        </template>
        Copy embed code
      </ak-tooltip>
    </ak-display-table-cell>

    <ak-display-table-cell
      v-if="appType === 'forms'"
      class="text-right palm--hidden text-nowrap cell--1"
    >
      <ak-tooltip>
        <template #trigger>
          <ak-button
            circle
            size="small"
            color="white"
            tag="router-link"
            :to="{ name: 'formPerformance', params: { appId: app._id } }"
          >
            <ak-icon type="connection-bars" size="big" />
          </ak-button>
        </template>
        Form Performance
      </ak-tooltip>
    </ak-display-table-cell>

    <ak-display-table-cell
      v-if="appType === 'pages'"
      class="text-right palm--hidden text-nowrap cell--1"
    >
      <ak-tooltip v-if="app.publishedStatus && !app.hidden">
        <template #trigger>
          <ak-button
            circle
            size="small"
            color="white"
            @click.stop="copyShortUrl(app)"
          >
            <ak-icon type="link" size="big" />
          </ak-button>
        </template>
        Copy link
      </ak-tooltip>
    </ak-display-table-cell>

    <ak-display-table-cell
      v-if="appType === 'pages'"
      class="text-right palm--hidden text-nowrap cell--1"
    >
      <ak-tooltip v-if="app.publishedStatus && !app.hidden">
        <template #trigger>
          <ak-button
            circle
            size="small"
            color="white"
            @click.stop="copyEmbedCode(app)"
          >
            <ak-icon type="code" size="big" />
          </ak-button>
        </template>
        Copy embed code
      </ak-tooltip>
    </ak-display-table-cell>

    <ak-display-table-cell>
      <ak-apps-list-actions
        :app="app"
        :app-link="appLink"
        :app-settings="appSettings"
        :app-type="appType"
        :delete-app="deleteApp"
        :active-account="activeAccount"
        :active-agency="activeAgency"
        :clone-app="cloneApp"
        :create-url="createUrl"
        :url-builder-enabled="urlBuilderEnabled"
        :current-view="currentView"
      />
    </ak-display-table-cell>
  </ak-display-table-row>
</template>

<script>
import DisplayTableRow from '@akerolabs/akero-vue/src/components/displayTable/DisplayTableRow.vue'
import DisplayTableCell from '@akerolabs/akero-vue/src/components/displayTable/DisplayTableCell.vue'
import Pack from '@akerolabs/akero-vue/src/components/pack/Pack.vue'
import PackItem from '@akerolabs/akero-vue/src/components/pack/PackItem.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import Tooltip from '@akerolabs/akero-vue/src/components/tooltip/Tooltip.vue'
import AppStatusTag from '../AppStatusTag.vue'
import AppsListActions from './AppsListActions.vue'
import appsListRowMixin from './appsListRowMixin.js'
import { defineComponent } from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-apps-list-table-row />
   */
  name: 'AkAppsListTableRow',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [DisplayTableRow.name]: DisplayTableRow,
    [DisplayTableCell.name]: DisplayTableCell,
    [Pack.name]: Pack,
    [PackItem.name]: PackItem,
    [Button.name]: Button,
    [Icon.name]: Icon,
    [Tooltip.name]: Tooltip,
    [AppStatusTag.name]: AppStatusTag,
    [AppsListActions.name]: AppsListActions,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Object}
     * @description The active account (drilled in or users)
     */
    activeAccount: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {Object}
     * @description The active agency (drilled in or users)
     */
    activeAgency: {
      default: () => ({}),
      type: Object,
    },

    app: {
      default: () => ({}),
      type: Object,
    },

    appType: {
      default: 'pages',
      type: String,
    },

    domains: {
      default: '',
      type: String,
    },

    deleteApp: {
      default: () => {},
      type: Function,
    },

    cloneApp: {
      default: () => {},
      type: Function,
    },

    createUrl: {
      default: () => {},
      type: Function,
    },

    urlBuilderEnabled: {
      default: false,
      type: Boolean,
    },
    currentView: {
      type: String,
      default: undefined,
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const mixin = appsListRowMixin({ props, emit })
    return {
      ...mixin,
    }
  },
})
</script>
